import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import './styles.css';
import ChevronDownIcon from "../../../assets/icons/ChevronDownIcon";
import colors from "../../../utils/Colors";
import SearchInput from "../SearchInput";
import Checkbox from '../Checkbox';
import SilverButton from "../../buttons/SilverButton";
import CheckCheckIcon from "../../../assets/icons/CheckCheckIcon";
import Translate from "../../../components/Translate";

// the menu stands this far clear of the control it belongs to
const MENU_GAP = 6;

const DropdownSelect = ({
                            options = [],
                            selectedValue,
                            onChange,
                            renderIndicator,
                            renderLabel,
                            children,
                            styles = {},
                            childStyles = {},
                            optionsContainerStyles = {},
                            itemStyles = {},
                            classes = '',
                            searchBy = null,
                            onSelectAll = null,
                            variant = 'default',
                            disabled = false,
                            direction = 'bottom',
                            overlay = false,
                            disableOption = null,
                            closeOnOptionSelect = false,
                        }) => {

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [search, setSearch] = useState('');
    const searchInputRef = useRef(null);
    const [firstTimeOpened, setFirstTimeOpened] = useState(false);
    const [isMenuContentMounted, setIsMenuContentMounted] = useState(false);
    const dropdownContainerRef = useRef(null);
    const hideTimerRef = useRef(null);

    /*
     * Which way the menu actually opens.
     *
     * `direction` is what the caller asked for; this is what there is room for.
     * A select on the last row of a table had its menu hanging off the bottom of
     * the page - which grew the page and put a scrollbar under the hand that was
     * reaching for an option - when the same menu fits perfectly well above the
     * control.
     *
     * The caller's choice still stands wherever it fits: this only overrules it
     * when the side it names has no room and the other side does.
     */
    const [openDirection, setOpenDirection] = useState(direction);

    const resolveDirection = useCallback(() => {
        if (!dropdownRef.current || !dropdownContainerRef.current) return direction;

        const trigger = dropdownRef.current.getBoundingClientRect();
        const needed = dropdownContainerRef.current.offsetHeight + MENU_GAP;

        const roomBelow = window.innerHeight - trigger.bottom;
        const roomAbove = trigger.top;

        if (direction === 'top') {
            return roomAbove >= needed || roomAbove >= roomBelow ? 'top' : 'bottom';
        }

        return roomBelow >= needed || roomBelow >= roomAbove ? 'bottom' : 'top';
    }, [direction]);

    const positionOverlayMenu = useCallback(() => {
        if (!overlay || !dropdownRef.current || !dropdownContainerRef.current) return;

        const trigger = dropdownRef.current.getBoundingClientRect();
        const menu = dropdownContainerRef.current;

        menu.style.left = `${trigger.left}px`;
        menu.style.right = 'auto';
        menu.style.width = `${trigger.width}px`;

        if (resolveDirection() === 'top') {
            menu.style.top = 'auto';
            menu.style.bottom = `${window.innerHeight - trigger.top + MENU_GAP}px`;
        } else {
            menu.style.top = `${trigger.bottom + MENU_GAP}px`;
            menu.style.bottom = 'auto';
        }
    }, [overlay, resolveDirection]);

    const filteredOptions = useMemo(() => {
        if (!searchBy) return options
        return options.filter((option) => searchBy(option, search));
    }, [options, search, searchBy]);

    const toggleDropdown = () => {
        // Do not mount option content (notably inline SVG <defs>) inside a
        // display:none menu. Browsers resolve SVG fragment ids document-wide,
        // and hidden duplicate gradients/masks can make visible icons point at
        // the wrong definition until the menu is shown and SVGs are repainted.
        if (!isOpen) {
            setFirstTimeOpened(true);
            setIsMenuContentMounted(true);
        }
        setIsOpen(!isOpen);
    };

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        clearTimeout(hideTimerRef.current);

        if (isOpen) {
            setIsMenuContentMounted(true);
            document.addEventListener('click', handleClickOutside);
            document.addEventListener("keydown", handleKeyDown);
            if (searchBy && searchInputRef.current) {
                searchInputRef.current.focus();
            }
            if (dropdownContainerRef.current) {
                // measured only once d-n is off: a display:none menu has no
                // height, and the first open would always think it fits
                dropdownContainerRef.current.classList.remove("d-n");
                setOpenDirection(resolveDirection());
                positionOverlayMenu();
            }
        } else {
            if (firstTimeOpened) {
                hideTimerRef.current = setTimeout(() => {
                    if (dropdownContainerRef.current) dropdownContainerRef.current.classList.add('d-n')
                    setIsMenuContentMounted(false);
                }, 350);
            }
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        }
        return () => {
            clearTimeout(hideTimerRef.current);
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, firstTimeOpened, positionOverlayMenu]);

    useEffect(() => {
        if (!isOpen || !overlay) return undefined;

        positionOverlayMenu();
        window.addEventListener('resize', positionOverlayMenu);
        window.addEventListener('scroll', positionOverlayMenu, true);

        return () => {
            window.removeEventListener('resize', positionOverlayMenu);
            window.removeEventListener('scroll', positionOverlayMenu, true);
        };
    }, [isOpen, overlay, positionOverlayMenu]);

    return (
        <div style={styles} className={'dropdown-select-parent-div ' + (disabled ? 'disabled ' : '') + classes}
             ref={dropdownRef}>
            <div
                className={`dropdown ${isOpen ? 'show' : ''} ${variant}`}
                onClick={() => disabled ? '' : toggleDropdown()}
            >
                {renderIndicator && renderIndicator(selectedValue)}
                <span style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'left',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                }}>
                    {renderLabel ? renderLabel(selectedValue) :
                        selectedValue ?
                            selectedValue.label ? selectedValue.label : selectedValue.name ? selectedValue.name : selectedValue
                            :
                            ''
                    }
                </span>

                {/* currentColor, so the chevron follows whatever the trigger is
                    set to - it was pinned to one grey and stayed that grey on a
                    disabled or a hovered control */}
                {/* a class of its own, not :nth-child(3): the indicator above is
                    optional, and without it the chevron is the second child and
                    every rule written against the third silently stopped
                    matching - which is how it lost its turn on opening */}
                <ChevronDownIcon className="dropdown-chevron" width="16" height="16"
                                 stroke="currentColor" style={{flexShrink: 0}}/>

                {/*When applying opacity to dropdown-select-parent-div z-index collapsing happens and
                    dropdown z-index brakes, instead of opacity using this wrapper div*/}
                {disabled
                    &&
                    <div
                        style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            inset: '0',
                            background: 'var(--ds-bg-primary)',
                            opacity: '0.4',
                            cursor: 'not-allowed',
                            borderRadius: '8px',
                        }}
                    />
                }

            </div>
            <div
                style={childStyles}
                key={'dropdown-menu'}
                className={`dropdown-menu-select ${overlay ? 'dropdown-menu-select--overlay' : ''} ${isOpen ? 'show' : firstTimeOpened ? 'hidden' : 'd-n'} ${openDirection}`}
                ref={dropdownContainerRef}
            >
                {
                    searchBy &&
                    <div style={{padding: '12px', borderBottom: '1px solid var(--ds-border-primary)'}}>
                        <SearchInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            ref={searchInputRef}
                        />
                    </div>
                }

                {/*
                  * overflowX has to be said out loud. Setting only overflow-y
                  * makes the browser treat overflow-x as auto as well, so a row
                  * a pixel wider than the panel - which is what a scrollbar on
                  * the right makes every row - put a second scrollbar along the
                  * bottom. height: 100% is gone with it: the panel is sized by
                  * what is in it, so a child asking for all of that height had
                  * nothing to measure against.
                  */}
                <div style={{
                    maxHeight: '280px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    opacity: 1,
                    backgroundColor: 'var(--ds-bg-primary)',
                    borderRadius: '8px',
                    padding: '6px',
                    ...optionsContainerStyles
                }}>
                    {
                        isMenuContentMounted && (filteredOptions.length === 0 ?
                            <div style={{
                                textAlign: 'center',
                                padding: '20px',
                                color: colors.silverDark,
                                fontSize: '14px',
                            }}>
                                <Translate text={'noResultsFound'}/>
                            </div>
                            :
                            filteredOptions.map((option, index) => (
                                <div
                                    key={option.value || option.name || option.id || option || index}
                                    className={'dropdown-item ' + (disabled || (disableOption && disableOption(option)) ? 'disabled' : '')}
                                    style={{
                                        color: 'var(--ds-text-primary)',
                                        textAlign: 'start',
                                        ...itemStyles,
                                    }}
                                    onClick={() => {
                                        if (disabled || (disableOption && disableOption(option))) return
                                        onChange(option);
                                        if (!Array.isArray(selectedValue) || closeOnOptionSelect) {
                                            setIsOpen(false);
                                        }
                                    }}
                                >
                                    <React.Fragment>
                                        {
                                            Array.isArray(selectedValue) ?
                                                <div style={{
                                                    display: "flex",
                                                    justifyContent: "flex-start",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                    width: "100%",
                                                    minWidth: 0
                                                }}>
                                                    <Checkbox
                                                        checked={selectedValue.some(s => s.value === option.value)}
                                                        disabled={disabled || Boolean(disableOption && disableOption(option))}
                                                        size={16}
                                                        handleChange={() => {
                                                            onChange(option);
                                                            if (closeOnOptionSelect) setIsOpen(false);
                                                        }}
                                                        onClick={event => event.stopPropagation()}
                                                    />
                                                    <span className="dropdown-item__label">
                                                        {children ? children(option) : Object.hasOwn(option, 'label') ? option.label : Object.hasOwn(option, 'name') ? option.name : option}
                                                    </span>
                                                </div>
                                                :
                                                children ? children(option) : Object.hasOwn(option, 'label') ? option.label : Object.hasOwn(option, 'name') ? option.name : option
                                        }
                                    </React.Fragment>
                                </div>
                            )))
                    }
                </div>

                {
                    onSelectAll &&
                    <div style={{padding: '12px', borderTop: '1px solid var(--ds-border-primary)'}}>
                        <SilverButton
                            styles={{
                                fontSize: '14px',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                borderRadius: '6px',
                            }}
                            height={'36px'}
                            onClick={onSelectAll}
                            disabled={disabled}
                        >
                            <CheckCheckIcon width={18} height={18} style={{color: colors.silverDark}} stroke="currentColor"/>
                            <span style={{color: colors.silverExtraDark,}}>{selectedValue.length === options.length ?
                                <Translate text={'unselectAll'}/> : <Translate text={'selectAll'}/>}</span>
                        </SilverButton>
                    </div>
                }
            </div>
        </div>
    );
};

export default DropdownSelect;



// WEBPACK FOOTER //
// ./src/components-new/form/DropdownSelect/index.jsx